import { useMutation, useApolloClient } from '@apollo/client';
import { 
  CREATE_STUDIO, 
  CREATE_AVAILABILITY, 
  CREATE_STUDIO_EQUIPMENT,
  CREATE_PAID_EQUIPMENT_ITEM,
  DELETE_AVAILABILITY,
  DELETE_STUDIO_EQUIPMENT,
  DELETE_PAID_EQUIPMENT_ITEM,
  DELETE_STUDIO
} from '../graphql/queries/createStudioQueries';
import { setStudioExactLocation } from '../graphql/mutations/userStudioMutations';

import { refetchUserStudios } from '../util/refetchUserStudios';

const addStudioToStudiosByUserIDCache = (cache, newStudio) => {
  if (!newStudio?.id || !newStudio?.userID) return;

  cache.modify({
    fields: {
      studiosByUserID(existingData = { items: [] }, { args, readField }) {
        if (args?.userID && args.userID !== newStudio.userID) {
          return existingData;
        }

        if (existingData?.items) {
          const alreadyExists = existingData.items.some(
            (item) => (readField('id', item) ?? item?.id) === newStudio.id
          );
          if (alreadyExists) return existingData;

          return {
            ...existingData,
            items: [...existingData.items, newStudio],
          };
        }

        if (Array.isArray(existingData)) {
          const alreadyExists = existingData.some(
            (item) => (readField('id', item) ?? item?.id) === newStudio.id
          );
          if (alreadyExists) return existingData;
          return [...existingData, newStudio];
        }

        return existingData;
      },
    },
  });
};

export const buildCreateStudioInput = (studioData) => ({
  ...(studioData?.id && { id: studioData.id }),
  StudioName: studioData?.StudioName,
  images: studioData?.images,
  studioLocation: studioData?.studioLocation,
  pricePerHour: studioData?.pricePerHour,
  image: studioData?.images?.[0],
  description: studioData?.description,
  userID: studioData?.userID,
  studioTypes: studioData?.studioTypes,
  tagValues: studioData?.tagValues || [],
  studioSize: studioData?.studioSize,
  numberOfPeople: studioData?.numberOfPeople,
  amenities: studioData?.amenities,
  city: studioData?.city,
  postcode: String(studioData?.postcode || ''),
  country: studioData?.country,
  minNumberOfHours: studioData?.minNumberOfHours,
  pricingMode: studioData?.pricingMode,
  tieredPricing: studioData?.tieredPricing,
  packages: studioData?.packages,
  discountRules: studioData?.discountRules,
  weekendSurcharge: studioData?.weekendSurcharge,
  extraFees: studioData?.extraFees,
  productionAreaSize: studioData?.productionAreaSize,
  ...(studioData?.cancellationPolicy && { cancellationPolicy: studioData.cancellationPolicy }),
  ...(studioData?.houseRules && { houseRules: studioData.houseRules }),
});

export const buildCreatePaidEquipmentItemInput = ({ studioId, item, index }) => ({
  ...(item?.id && { id: item.id }),
  studioID: studioId,
  category: item?.category,
  name: item?.name,
  price: item?.price,
  quantity: item?.quantity,
  description: item?.description || null,
  imageKey: item?.imageKey || null,
  sortOrder: item?.sortOrder ?? index,
});

export const useCreateStudio = () => {
  const client = useApolloClient();

  const [createStudio, { loading: studioLoading, error: studioError }] = useMutation(CREATE_STUDIO, {
    update(cache, { data }) {
      const newStudio = data?.createStudio;
      if (newStudio) {
        addStudioToStudiosByUserIDCache(cache, newStudio);
      }
    },
  });
  const [createAvailability] = useMutation(CREATE_AVAILABILITY);
  const [createEquipment] = useMutation(CREATE_STUDIO_EQUIPMENT);
  const [createPaidEquipmentItem] = useMutation(CREATE_PAID_EQUIPMENT_ITEM);
  const [deleteAvailability] = useMutation(DELETE_AVAILABILITY);
  const [deleteEquipment] = useMutation(DELETE_STUDIO_EQUIPMENT);
  const [deletePaidEquipmentItem] = useMutation(DELETE_PAID_EQUIPMENT_ITEM);
  const [deleteStudio] = useMutation(DELETE_STUDIO);

  const OPENCAGE_API_KEY = process.env.REACT_APP_OPENCAGE_API_KEY;

  const getCoordinates = async (address) => {
    try {
      const response = await fetch(
        `https://api.opencagedata.com/geocode/v1/json?q=${encodeURIComponent(address)}&key=${OPENCAGE_API_KEY}`
      );
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry;
        return { latitude: lat, longitude: lng };
      }
      throw new Error('No coordinates found for this address');
    } catch (error) {
      console.error('Geocoding error:', error);
      throw error;
    }
  };

  const coerceCoordinates = (coordinates) => {
    if (!coordinates) return null;
    const latitude = coordinates.latitude ?? coordinates.lat;
    const longitude = coordinates.longitude ?? coordinates.lng;

    if (!Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude))) {
      return null;
    }

    return {
      latitude: Number(latitude),
      longitude: Number(longitude),
    };
  };

  const resolveCoordinates = async (studioData) => {
    const validatedCoordinates = coerceCoordinates({
      latitude: studioData?.latitude,
      longitude: studioData?.longitude,
    });

    if (validatedCoordinates) {
      return validatedCoordinates;
    }

    if (!OPENCAGE_API_KEY) {
      throw new Error('Adresse muss vor dem Veröffentlichen erfolgreich validiert werden.');
    }

    const fullAddress = `${studioData?.streetAndNumber}, ${studioData?.city}, ${studioData?.postcode}, ${studioData?.country}`;
    console.warn('[useCreateStudio] Falling back to OpenCage because no validated coordinates were provided.');
    return getCoordinates(fullAddress);
  };

  const buildExactLocationInput = ({ studioID, studioData, coordinates }) => ({
    studioID,
    streetAndNumber: studioData?.streetAndNumber || null,
    adressLine2: studioData?.adressLine2 || null,
    city: studioData?.city,
    postcode: String(studioData?.postcode || ''),
    country: studioData?.country,
    latitude: coordinates?.latitude ?? null,
    longitude: coordinates?.longitude ?? null,
  });

  const rollbackCreatedStudio = async (created) => {
    const rollbackErrors = [];
    const safeDelete = async (label, mutation, id) => {
      if (!id) return;
      try {
        await mutation({ variables: { input: { id } } });
      } catch (error) {
        rollbackErrors.push({ label, id, message: error?.message || 'Rollback failed' });
      }
    };

    for (const id of [...created.paidEquipmentItems].reverse()) {
      await safeDelete('paidEquipmentItem', deletePaidEquipmentItem, id);
    }
    for (const id of [...created.equipment].reverse()) {
      await safeDelete('equipment', deleteEquipment, id);
    }
    for (const id of [...created.availabilities].reverse()) {
      await safeDelete('availability', deleteAvailability, id);
    }
    await safeDelete('studio', deleteStudio, created.studioId);

    if (rollbackErrors.length > 0) {
      console.warn('[useCreateStudio] Publish rollback had cleanup errors:', rollbackErrors);
    }
  };

    const handleStudioCreation = async (studioData) => {
        const created = {
          studioId: null,
          availabilities: [],
          equipment: [],
          paidEquipmentItems: [],
        };
        let createdStudio = null;

        try {
        const coordinates = await resolveCoordinates(studioData);
        const { data: studioResponse } = await createStudio({
        variables: {
          input: buildCreateStudioInput(studioData)
        }
      });

      const studioId = studioResponse.createStudio.id;
      created.studioId = studioId;
      createdStudio = studioResponse.createStudio;

      const { data: exactLocationResponse } = await client.mutate({
        mutation: setStudioExactLocation,
        variables: {
          input: buildExactLocationInput({
            studioID: studioId,
            studioData,
            coordinates,
          }),
        },
      });

      if (exactLocationResponse?.setStudioExactLocation) {
        createdStudio = {
          ...createdStudio,
          ...exactLocationResponse.setStudioExactLocation,
        };
      }

      if (studioData.availability) {
        const dayMapping = {
          Mon: 1,
          Di: 2,
          Mi: 3,
          Do: 4,
          Fr: 5,
          Sa: 6,
          So: 7
        };
  
        const formattedAvailabilities = Object.entries(studioData.availability)
          .filter(([_, dayData]) => dayData.isActive)
          .flatMap(([day, dayData]) => {
            if (dayData.is24Hours) {
              return [{
                studioID: studioId,
                dayOfWeek: dayMapping[day],
                startTime: "00:00",
                endTime: "23:59"
              }];
            }
            
            return dayData.timeSlots
              .filter(slot => slot.start && slot.end)
              .map(slot => ({
                studioID: studioId,
                dayOfWeek: dayMapping[day],
                startTime: slot.start,
                endTime: slot.end
              }));
          });

          for (const availability of formattedAvailabilities) {
            const { data } = await createAvailability({
                variables: {
                  input: availability
                }
              });
            if (data?.createAvailability?.id) {
              created.availabilities.push(data.createAvailability.id);
            }
          }
        }

        if (studioData?.equipment && Object.keys(studioData?.equipment).length > 0) {
          for (const [category, items] of Object.entries(studioData?.equipment)) {
            const { data } = await createEquipment({
                variables: {
                  input: {
                    studioID: studioId,
                    category: category,
                    items: items
                  }
                }
              });
            if (data?.createStudioEquipment?.id) {
              created.equipment.push(data.createStudioEquipment.id);
            }
          }
        }

        if (Array.isArray(studioData?.paidEquipmentItems) && studioData.paidEquipmentItems.length > 0) {
          for (const [index, item] of studioData.paidEquipmentItems.entries()) {
            const { data } = await createPaidEquipmentItem({
                variables: {
                  input: buildCreatePaidEquipmentItemInput({ studioId, item, index })
                }
              });
            if (data?.createPaidEquipmentItem?.id) {
              created.paidEquipmentItems.push(data.createPaidEquipmentItem.id);
            }
          }
        }
    } catch (error) {
      console.error('Error creating studio:', error);
      if (created.studioId) {
        await rollbackCreatedStudio(created);
      }
      throw error;
    }

    try {
      await refetchUserStudios(client);
    } catch (refetchError) {
      console.warn('[useCreateStudio] Studio was created, but refetch failed:', refetchError);
    }

    return createdStudio;
  };

  return {
    handleStudioCreation,
    loading: studioLoading,
    error: studioError
  };
};
